import { Paragraph } from '@components/ui/Paragraph/Paragraph';
import { useSiteInfo } from '@context/siteInfoContext';
import { useEffect, useState } from 'react';
import type { Product, Variant } from 'types';

import type { MarketConfiguration } from '@/config/markets/const';
import { cn } from '@/lib/utils';
import { getGooglePriceForVariant } from '@/utils/googlePricing';
import { formatPrice } from '@/utils/priceUtils';

export default function Price({
    className,
    product,
    market,
}: {
    className?: string;
    product?: Product | Variant;
    market: MarketConfiguration;
}) {
    const { dictionary: globalDictionary } = useSiteInfo();

    const [GADPrice, setGADPrice] = useState<number>();

    useEffect(() => {
        if (product?.type === 'Variant') {
            const personalPrice = getGooglePriceForVariant(market, product?.id);
            if (personalPrice) setGADPrice(personalPrice);
            return () => setGADPrice(undefined);
        }
        if (product?.hasVariants) {
            const priceForVariants = product.VariantCode.map((id: string) =>
                getGooglePriceForVariant(market, id),
            ).filter(Boolean);
            if (priceForVariants.length)
                setGADPrice(Math.min(product.price, ...priceForVariants));
            return () => setGADPrice(undefined);
        }
    }, [
        market,
        product?.type,
        product?.id,
        product?.hasVariants,
        product?.VariantCode,
        product?.price,
    ]);

    if (!product) return null;

    const isVariant = product.type == 'Variant';
    const hasGADPrice = typeof GADPrice === 'number';

    const price = hasGADPrice
        ? GADPrice
        : isVariant
          ? (product.price ?? 0)
          : (product.presentationPrice ?? 0);

    return (
        <Paragraph className={cn('flex gap-x-1', className)}>
            <>
                <span className="font-medium">
                    {product.fromPrice && globalDictionary.from}{' '}
                    {formatPrice(price, market.locale)}
                </span>
                {(product.priceType !== 'standard' || hasGADPrice) && (
                    <span className="text-grey-300 line-through">
                        {formatPrice(
                            isVariant
                                ? (product.rrpWhenCampaign ??
                                      product.originalPrice ??
                                      0)
                                : (product.presentationRrpWhenCampaign ??
                                      product.originalPrice ??
                                      product.presentationPrice ??
                                      0),
                            market.locale,
                        )}
                    </span>
                )}
            </>
        </Paragraph>
    );
}
